/**
 * G1 — Home.
 * 1:1 with assets/extracted/tier-1/G1-home.html.
 *
 * States:
 *  - default  — unsolved, today's CTA shows "Solve today" in rarity color
 *  - solved   — already solved today, CTA mutes to ink + Practice link shown
 *  - at-risk  — UTC reset < 2h away + streak alive, ring pulses around CTA
 *  - day-1    — no streak yet, streak row hidden
 */
import { generateDailyPuzzle, todaySeed } from '../../engine/generate';
import { regionName, t } from '../../i18n/runtime';
import { profile, refillCreditsIfNewDay } from '../../state/profile';
import { iconString } from '../icons';
import { el, html, on } from '../html';
import type { RouteContext } from '../router';
import { tabbar } from '../tabbar';

const RARITY_SYMBOL: Record<string, string> = {
  common: '●',
  uncommon: '◆',
  rare: '▲',
  epic: '★',
  legendary: '✦',
};
const RARITY_LABEL: Record<string, string> = {
  common: 'COMMON',
  uncommon: 'UNCOMMON',
  rare: 'RARE',
  epic: 'EPIC',
  legendary: 'LEGENDARY',
};

function flagEmoji(country: string): string {
  if (country.length !== 2) return '🏳️';
  const a = country.charCodeAt(0) - 65;
  const b = country.charCodeAt(1) - 65;
  if (a < 0 || a > 25 || b < 0 || b > 25) return '🏳️';
  return String.fromCodePoint(0x1f1e6 + a) + String.fromCodePoint(0x1f1e6 + b);
}

function isStreakAtRisk(lastSolveDate: string | null): boolean {
  if (!lastSolveDate) return false;
  const now = new Date();
  return 24 - now.getUTCHours() <= 2;
}

export default function (ctx: RouteContext): HTMLElement {
  const today = todaySeed();
  refillCreditsIfNewDay(today);
  const p = profile().get();
  const puzzle = generateDailyPuzzle(today);
  const alreadySolved = p.lastSolveDate === today;
  const stateAttr = alreadySolved
    ? 'solved'
    : p.streak === 0
      ? 'day-1'
      : isStreakAtRisk(p.lastSolveDate)
        ? 'at-risk'
        : 'default';
  const ctaText = alreadySolved ? t('home.cta_review') : t('home.cta_play');
  const ctaGlyph = alreadySolved ? iconString('refresh', 14) : iconString('play', 14);

  const root = el(html`
    <section class="screen-home" data-state="${stateAttr}" data-rarity="${puzzle.rarity}" aria-label="${t('home.today_label')}">
      <button class="home-hamburger" aria-label="${t('nav.menu')}" data-menu>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 6 H21 M3 12 H21 M3 18 H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="home-content">
        <div class="home-hero">
          <div class="home-eyebrow">${t('home.today_label')}</div>
          <div class="home-rarity-symbol rarity-${puzzle.rarity}" aria-hidden="true">${RARITY_SYMBOL[puzzle.rarity]}</div>
          <div class="home-rarity-label rarity-${puzzle.rarity}">${RARITY_LABEL[puzzle.rarity]}</div>
        </div>
        <div class="home-below">
          <div class="home-cta-wrap">
            <span class="home-ring" aria-hidden="true"></span>
            <button class="home-cta" data-play>
              <span class="home-cta-glyph">${ctaGlyph}</span>
              <span>${ctaText}</span>
            </button>
          </div>
          <button class="home-practice-link" data-practice>${t('home.cta_practice')}</button>
          ${p.streak > 0 ? html`<div class="home-streak">${t('home.streak', { n: p.streak })}</div>` : ''}
          <div class="home-country">${t('home.country_rank', { flag: flagEmoji(p.country), country: regionName(p.country), rank: 0 })}</div>
          ${!alreadySolved ? html`<div class="home-country-sub">${t('home.country_sub')}</div>` : ''}
        </div>
      </div>
      <div data-tabbar></div>
    </section>
  `);
  // Apply rarity color via CSS variable on the host so .home-cta and other
  // rarity-tinted elements pick up the same hue per puzzle.
  root.style.setProperty('--rarity', `var(--rarity-${puzzle.rarity})`);

  const tb = root.querySelector('[data-tabbar]') as HTMLElement;
  tb.replaceWith(tabbar('home', (route) => ctx.navigate(route)));

  on(root, 'click', '[data-play]', () => {
    if (alreadySolved) ctx.navigate(`/review/${today}`);
    else ctx.navigate(`/play/${today}`);
  });
  on(root, 'click', '[data-practice]', () => ctx.navigate('/practice'));
  on(root, 'click', '[data-menu]', () => ctx.navigate('/settings'));
  return root;
}
